import { create } from "zustand"
import api from "../services/api"
import { montarPayloadReativacaoUsuario } from "./usuarioReativacao.helpers"
import { useDashboardStore } from "./dashboardStore"

const usuarioPaginadaChaveGerar = (cBusca = "", nPagina = 1, mFilas = [], lAtivo = true, lIncluirInativos = false) =>
  `${cBusca}|${nPagina}|${mFilas.join(",")}|${lAtivo}|${lIncluirInativos}`

const usuarioListaSimplesChaveGerar = (lValidarPermissaoPerfil = false, nCompanyId = "") =>
  `${lValidarPermissaoPerfil}|${nCompanyId}`

const usuarioCorrespondeBusca = (cBusca, oUsuario) => {
  if (!cBusca) return true
  const cTermo = cBusca.toLowerCase().trim()
  return (
    (oUsuario.name || "").toLowerCase().includes(cTermo) || (oUsuario.email || "").toLowerCase().includes(cTermo)
  )
}

const usuarioAtualizarNaLista = (mLista, oUsuario) => {
  const nIndice = mLista.findIndex((oItem) => oItem.id === oUsuario.id)
  if (nIndice === -1) {
    return [...mLista, oUsuario]
  }
  const mNova = [...mLista]
  mNova[nIndice] = { ...mNova[nIndice], ...oUsuario }
  return mNova
}

const usuarioRemoverDaLista = (mLista, nId) => mLista.filter((oItem) => oItem.id !== nId)

const usuarioOrdenarAtivosPrimeiro = (mLista) =>
  [...mLista].sort((oA, oB) => {
    const nPesoA = oA.ativo === false ? 1 : 0
    const nPesoB = oB.ativo === false ? 1 : 0
    if (nPesoA !== nPesoB) return nPesoA - nPesoB
    return (oA.name || "").localeCompare(oB.name || "", "pt-BR", { sensitivity: "base" })
  })

export const useUsuarioStore = create((set, get) => ({
  nGeracaoCache: 0,
  oListaSimplesPorChave: {},
  oPaginadoPorChave: {},
  lPaginadoInvalido: true,
  lListaSimplesInvalida: true,
  oBuscaListaSimplesPendentePorChave: {},
  oBuscaPaginadaPendentePorChave: {},

  reiniciar: () =>
    set((oEstado) => ({
      nGeracaoCache: oEstado.nGeracaoCache + 1,
      oListaSimplesPorChave: {},
      oPaginadoPorChave: {},
      lPaginadoInvalido: true,
      lListaSimplesInvalida: true,
      oBuscaListaSimplesPendentePorChave: {},
      oBuscaPaginadaPendentePorChave: {},
    })),

  invalidarTudo: () =>
    set((oEstado) => ({
      nGeracaoCache: oEstado.nGeracaoCache + 1,
      oListaSimplesPorChave: {},
      oPaginadoPorChave: {},
      lPaginadoInvalido: true,
      lListaSimplesInvalida: true,
      oBuscaListaSimplesPendentePorChave: {},
      oBuscaPaginadaPendentePorChave: {},
    })),

  listaSimplesBuscar: async ({
    validarPermissaoPerfil: lValidarPermissaoPerfil = false,
    companyId: nCompanyId,
    force: lForcar = false,
  } = {}) => {
    const cChave = usuarioListaSimplesChaveGerar(lValidarPermissaoPerfil, nCompanyId)
    const oEstado = get()

    if (!lForcar && !oEstado.lListaSimplesInvalida && oEstado.oListaSimplesPorChave[cChave]) {
      return oEstado.oListaSimplesPorChave[cChave]
    }

    if (oEstado.oBuscaListaSimplesPendentePorChave[cChave]) {
      return oEstado.oBuscaListaSimplesPendentePorChave[cChave]
    }

    const nGeracao = oEstado.nGeracaoCache
    const oPromessa = api
      .get("/users/list", {
        params: {
          validarPermissaoPerfil: lValidarPermissaoPerfil ? "true" : undefined,
          companyId: nCompanyId,
          somenteAtivos: true,
        },
      })
      .then(({ data }) => {
        if (get().nGeracaoCache !== nGeracao) {
          return []
        }
        const mLista = data || []
        const mListaAtivos = mLista.filter((oUsuario) => oUsuario.ativo !== false)
        set((oAtual) => ({
          oListaSimplesPorChave: { ...oAtual.oListaSimplesPorChave, [cChave]: mListaAtivos },
          lListaSimplesInvalida: false,
          oBuscaListaSimplesPendentePorChave: { ...oAtual.oBuscaListaSimplesPendentePorChave, [cChave]: undefined },
        }))
        return mListaAtivos
      })
      .catch((oErro) => {
        if (get().nGeracaoCache === nGeracao) {
          set((oAtual) => ({
            oBuscaListaSimplesPendentePorChave: { ...oAtual.oBuscaListaSimplesPendentePorChave, [cChave]: undefined },
          }))
        }
        throw oErro
      })

    set((oAtual) => ({
      oBuscaListaSimplesPendentePorChave: { ...oAtual.oBuscaListaSimplesPendentePorChave, [cChave]: oPromessa },
    }))
    return oPromessa
  },

  listaSimplesObter: async (oParametros) => get().listaSimplesBuscar(oParametros),

  usuarioInativacaoImpactoObter: async (nUserId) => {
    const { data } = await api.get(`/users/${nUserId}/inativacao-impacto`)
    return data
  },

  usuarioInativar: async (nUserId, lConfirmarConversasAbertas = false) => {
    const { data } = await api.post(`/users/${nUserId}/inativar`, {
      confirmarConversasAbertas: lConfirmarConversasAbertas,
    })
    useDashboardStore.getState().invalidarTudo()
    return data
  },

  usuarioReativar: async (nUserId) => {
    const { data: oUsuario } = await api.get(`/users/${nUserId}`)
    const { data } = await api.put(`/users/${nUserId}`, montarPayloadReativacaoUsuario(oUsuario))
    return data
  },

  paginadaBuscar: async ({
    searchParam: cBusca = "",
    pageNumber: nPagina = 1,
    filas: mFilas = [],
    ativo: lAtivo = true,
    incluirInativos: lIncluirInativos = false,
    force: lForcar = false,
  } = {}) => {
    const cChave = usuarioPaginadaChaveGerar(cBusca, nPagina, mFilas, lAtivo, lIncluirInativos)
    const oEstado = get()

    if (!lForcar && !oEstado.lPaginadoInvalido && oEstado.oPaginadoPorChave[cChave]) {
      return oEstado.oPaginadoPorChave[cChave]
    }

    if (oEstado.oBuscaPaginadaPendentePorChave[cChave]) {
      return oEstado.oBuscaPaginadaPendentePorChave[cChave]
    }

    const nGeracao = oEstado.nGeracaoCache
    const oParams = {
      searchParam: cBusca,
      pageNumber: nPagina,
      filas: mFilas.length > 0 ? JSON.stringify(mFilas) : undefined,
    }

    if (lIncluirInativos) {
      oParams.incluirInativos = true
    } else {
      oParams.ativo = lAtivo
    }

    const oPromessa = api
      .get("/users/", {
        params: oParams,
      })
      .then(({ data }) => {
        if (get().nGeracaoCache !== nGeracao) {
          return { users: [], hasMore: false, count: 0 }
        }
        const oPayload = {
          users: lIncluirInativos ? usuarioOrdenarAtivosPrimeiro(data.users || []) : data.users || [],
          hasMore: data.hasMore,
          count: data.count,
        }
        set((oAtual) => ({
          oPaginadoPorChave: { ...oAtual.oPaginadoPorChave, [cChave]: oPayload },
          lPaginadoInvalido: false,
          oBuscaPaginadaPendentePorChave: { ...oAtual.oBuscaPaginadaPendentePorChave, [cChave]: undefined },
        }))
        return oPayload
      })
      .catch((oErro) => {
        if (get().nGeracaoCache === nGeracao) {
          set((oAtual) => ({
            oBuscaPaginadaPendentePorChave: { ...oAtual.oBuscaPaginadaPendentePorChave, [cChave]: undefined },
          }))
        }
        throw oErro
      })

    set((oAtual) => ({
      oBuscaPaginadaPendentePorChave: { ...oAtual.oBuscaPaginadaPendentePorChave, [cChave]: oPromessa },
    }))
    return oPromessa
  },

  paginadaObter: async (oParametros) => get().paginadaBuscar(oParametros),

  eventoSocketAplicar: (oDados) => {
    const { action: cAcao, user: oUsuario, userId: nUserId } = oDados || {}

    set((oEstado) => {
      let oListaSimplesPorChave = { ...oEstado.oListaSimplesPorChave }
      let oPaginadoPorChave = { ...oEstado.oPaginadoPorChave }

      const aplicarEmLista = (mLista, oItem, lIncluir) => {
        if (lIncluir) {
          return usuarioAtualizarNaLista(mLista, oItem)
        }
        return usuarioRemoverDaLista(mLista, oItem.id)
      }

      Object.keys(oListaSimplesPorChave).forEach((cChaveLista) => {
        let mLista = oListaSimplesPorChave[cChaveLista] || []

        if (cAcao === "delete") {
          mLista = usuarioRemoverDaLista(mLista, Number(nUserId))
        } else if (oUsuario && (cAcao === "create" || cAcao === "update")) {
          const lUsuarioAtivo = oUsuario.ativo !== false
          if (!lUsuarioAtivo) {
            mLista = usuarioRemoverDaLista(mLista, oUsuario.id)
          } else {
            mLista = aplicarEmLista(mLista, oUsuario, true)
          }
        }

        oListaSimplesPorChave[cChaveLista] = mLista
      })

      Object.keys(oPaginadoPorChave).forEach((cChave) => {
        const oEntrada = oPaginadoPorChave[cChave]
        if (!oEntrada?.users) return

        const [cBusca, , , , cIncluirInativos] = cChave.split("|")
        const lIncluirInativos = cIncluirInativos === "true"
        const lSomenteAtivos = !lIncluirInativos
        let mUsers = oEntrada.users

        if (cAcao === "delete") {
          mUsers = usuarioRemoverDaLista(mUsers, Number(nUserId))
        } else if (oUsuario && (cAcao === "create" || cAcao === "update")) {
          const lUsuarioAtivo = oUsuario.ativo !== false

          if (lSomenteAtivos && !lUsuarioAtivo) {
            mUsers = usuarioRemoverDaLista(mUsers, oUsuario.id)
          } else if (!usuarioCorrespondeBusca(cBusca, oUsuario)) {
            mUsers = usuarioRemoverDaLista(mUsers, oUsuario.id)
          } else if (cAcao === "create" && lSomenteAtivos && lUsuarioAtivo) {
            mUsers = usuarioAtualizarNaLista(mUsers, oUsuario)
          } else if (cAcao === "update") {
            mUsers = usuarioAtualizarNaLista(mUsers, oUsuario)
          }

          if (lIncluirInativos) {
            mUsers = usuarioOrdenarAtivosPrimeiro(mUsers)
          }
        }

        oPaginadoPorChave[cChave] = { ...oEntrada, users: mUsers }
      })

      return {
        oListaSimplesPorChave,
        oPaginadoPorChave,
        lListaSimplesInvalida: oEstado.lListaSimplesInvalida,
        lPaginadoInvalido: oEstado.lPaginadoInvalido,
      }
    })
  },
}))

export { usuarioPaginadaChaveGerar, usuarioListaSimplesChaveGerar, usuarioCorrespondeBusca, usuarioOrdenarAtivosPrimeiro }
